import React, {
  createContext,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useState,
} from 'react';
import cn from '../../utils/cn';
import { type EditorOptions, generateHTML as generateHTMLPrimitive } from '@tiptap/core';
import { type Editor, EditorContent, useEditor, type JSONContent } from '@tiptap/react';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import TiptapBold from './tiptap-toolbar-items/tiptap-bold';
import TiptapItalic from './tiptap-toolbar-items/tiptap-italic';
import TiptapUnderline from './tiptap-toolbar-items/tiptap-underline';
import TiptapStrike from './tiptap-toolbar-items/tiptap-strike';
import TiptapTextAlignGroup from './tiptap-toolbar-items/tiptap-text-align-group';
import TiptapListGroup from './tiptap-toolbar-items/tiptap-list-group';
import TiptapLink from './tiptap-toolbar-items/tiptap-link/tiptap-link';

export type TiptapRange = {
  from: number;
  to: number;
};

export type TiptapEditor = Editor;

type TiptapContextType = {
  editor: Editor;
  linkPopoverOpen: boolean;
  handleSetLinkPopoverOpen: (open: boolean) => void;
};

const TiptapContext = createContext<TiptapContextType | null>(null);

export const useTiptap = () => {
  const context = useContext(TiptapContext);

  if (!context) {
    throw new Error('useTiptap must be used within a Tiptap.Root');
  }

  return context;
};

const buildExtensions = ({ placeholder }: { placeholder?: string }) => [
  StarterKit.configure({
    link: {
      openOnClick: false,
      defaultProtocol: 'https',
      protocols: ['http', 'https'],
    },
  }),
  Placeholder.configure({
    placeholder,
    emptyEditorClass:
      'before:content-[attr(data-placeholder)] before:text-gray-400 before:float-left before:h-0 before:pointer-events-none',
  }),
  CharacterCount.configure({
    textCounter: (text) => [...new Intl.Segmenter().segment(text.trim())].length,
  }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
];

/**
 * Generates sanitized HTML from TipTap JSON content without initializing
 * a full editor instance. Useful for displaying read-only content outside the editor context.
 */
export const generateHTML = (content: JSONContent) =>
  generateHTMLPrimitive(content, buildExtensions({}));

export type TiptapRootProps = {
  placeholder?: string;
} & Partial<EditorOptions> &
  Pick<React.ComponentPropsWithoutRef<'div'>, 'className' | 'children'>;

const Root = forwardRef<HTMLDivElement, TiptapRootProps>(
  ({ placeholder, content = '', className, children, ...props }, ref) => {
    const [linkPopoverOpen, setlinkPopoverOpen] = useState<boolean>(false);

    const handleSetLinkPopoverOpen = useCallback(
      (open: boolean) => setlinkPopoverOpen(open),
      [],
    );

    const editor = useEditor({
      extensions: buildExtensions({ placeholder }),
      content,
      editorProps: {
        attributes: {
          class: 'focus:outline-none size-full',
          role: 'textbox',
        },
        handleClick(_view, _pos, event) {
          const target = event.target as HTMLElement;

          if (target.tagName === 'A') {
            handleSetLinkPopoverOpen(true);
          }
        },
      },
      ...props,
    });

    if (!editor) {
      return null;
    }

    return (
      <TiptapContext.Provider
        value={{ editor, linkPopoverOpen, handleSetLinkPopoverOpen }}
      >
        <div ref={ref} className={cn('flex flex-col gap-3', className)}>
          {children}
        </div>
      </TiptapContext.Provider>
    );
  },
);

Root.displayName = 'Tiptap.Root';

// This component overrides the legacy hektor style to enable toolbar effects.
// Used by default by the tiptap ContentInput, you must use it to
// display the content correctly (like in ActivityFreeTalk.tsx).
const HtmlRenderer = memo(
  ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
    <div
      className={cn(
        // Force bold style cumulated with other styles
        '[&_strong_*]:[font-weight:inherit]',
        // Style ordered and bullet lists
        '[&_ol]:list-decimal [&_ol]:ps-6 [&_ul]:list-disc [&_ul]:ps-6',
        // Style links
        '[&_a:hover]:text-purple-700 [&_a]:cursor-pointer [&_a]:text-blue-500 [&_a]:underline',
        // Keep empty paragraph height, while Tiptap (ProseMirror) add a `<br />` in empty paragraph in the lib
        // @see https://github.com/ueberdosis/tiptap/issues/412
        String.raw`[&_p:empty]:after:content-['\00A0']`,
        className,
      )}
      {...props}
    />
  ),
);

HtmlRenderer.displayName = 'Tiptap.HtmlRenderer';

const Content = memo(
  ({
    className,
    ...props
  }: Omit<
    React.ComponentPropsWithoutRef<typeof EditorContent>,
    'editor' | 'children'
  >) => {
    const { editor } = useTiptap();

    return (
      <EditorContent
        editor={editor}
        className={cn(
          'h-full rounded-xl border border-neutral-300 bg-white p-4 text-sm',
          className,
        )}
        {...props}
      />
    );
  },
);

Content.displayName = 'Tiptap.Content';

export enum ToolbarSize {
  DEFAULT,
  SMALL,
}

export type ToolbarProps = {
  size?: ToolbarSize;
} & React.ComponentPropsWithoutRef<'div'>;

const ToolbarContext = createContext<{
  size: ToolbarSize;
}>({
  size: ToolbarSize.DEFAULT,
});

const useToolbar = () => useContext(ToolbarContext);

const Toolbar = memo(
  ({ size = ToolbarSize.DEFAULT, className, ...props }: ToolbarProps) => (
    <ToolbarContext.Provider value={{ size }}>
      <div
        className={cn(
          'flex items-center gap-4',
          { 'gap-1.5': size === ToolbarSize.SMALL },
          className,
        )}
        {...props}
      />
    </ToolbarContext.Provider>
  ),
);

Toolbar.displayName = 'Tiptap.Toolbar';

const SIZE_CLASS_MAP: Record<ToolbarSize, string> = {
  [ToolbarSize.DEFAULT]: '[&>button]:p-1.5 [&_svg]:size-4',
  [ToolbarSize.SMALL]: '[&>button]:p-2 [&_svg]:size-3.5',
};

const ToolbarGroup = memo(
  ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => {
    const { size } = useToolbar();
    const sizeClassName = SIZE_CLASS_MAP[size];

    return (
      <div
        className={cn(
          'flex w-fit items-center overflow-hidden rounded-md',
          sizeClassName,
          className,
        )}
        {...props}
      />
    );
  },
);

ToolbarGroup.displayName = 'Tiptap.Toolbar.Group';

const ToolbarCompound = Object.assign(Toolbar, {
  Group: ToolbarGroup,
  Bold: TiptapBold,
  Italic: TiptapItalic,
  Underline: TiptapUnderline,
  Strike: TiptapStrike,
  TextAlignGroup: TiptapTextAlignGroup,
  ListGroup: TiptapListGroup,
  Link: TiptapLink,
});

const Tiptap = Object.assign(
  {},
  {
    Root,
    HtmlRenderer,
    Content,
    Toolbar: ToolbarCompound,
  },
);

export default Tiptap;
